///
/// EarthBrowser
///
/// Created by Matt Giger on 9/19/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Installs the store-backed implementation of the engine's redux seam
/// (earthbrowser/engineBridge.ts). Called once from main.tsx before the
/// app renders, so the bridge is live before any engine code constructs.
/// The embed entry never imports this module, which is what keeps the
/// whole redux graph out of the embed bundle.
///

import { showStormAdvisoryToast } from "@/components/misc/StormAdvisoryToast"
import { showWeatherAlertToast } from "@/components/misc/WeatherAlertToast"
import { toast } from "@/components/ui/use-toast"
import { engineActions, installEngineBridge } from "@/earthbrowser/engineBridge"
import { installEngineNotifier } from "@/earthbrowser/engineNotifications"
import { setClockSpeedCap, setCameraMode, setActiveStory, addStoryDrawingPoint, setDropPinMenu, setWebcamSheet, setQuakeCutout, updateQuakeCutout, setStormCutout, updateStormCutout, setStormWindProbabilityKt } from "@/redux/dialogSlice"
import { sendCameraCommand, sendDatasetAction, setCameraState, setClockAnimatingSpeed, setClockCurrentTime, setClockEndOffset, setClockStartOffset, setClockState } from "@/redux/earthBrowserSlice"
import { setPaidEntitlement } from "@/redux/entitlementsSlice"
import { reduxActionBridge } from "@/redux/ReduxActionBridge"
import { appStore } from "@/redux/store"
import { openInfoWindow, updateSatellitePosition } from "@/redux/userInterfaceSlice"
import type { UnknownAction } from "@reduxjs/toolkit"

export function installReduxEngineBridge(): void {
	// The engine's action-type constants must match the real action
	// creators; fail fast at startup if a slice or reducer was renamed
	const pairs: [string, string][] = [
		[engineActions.setClockCurrentTime, setClockCurrentTime.type],
		[engineActions.setClockState, setClockState.type],
		[engineActions.setClockAnimatingSpeed, setClockAnimatingSpeed.type],
		[engineActions.setClockStartOffset, setClockStartOffset.type],
		[engineActions.setClockEndOffset, setClockEndOffset.type],
		[engineActions.setCameraState, setCameraState.type],
		[engineActions.sendDatasetAction, sendDatasetAction.type],
		[engineActions.sendCameraCommand, sendCameraCommand.type],
		[engineActions.setClockSpeedCap, setClockSpeedCap.type],
		[engineActions.setCameraMode, setCameraMode.type],
		[engineActions.setActiveStory, setActiveStory.type],
		[engineActions.addStoryDrawingPoint, addStoryDrawingPoint.type],
		[engineActions.setDropPinMenu, setDropPinMenu.type],
		[engineActions.setWebcamSheet, setWebcamSheet.type],
		[engineActions.setQuakeCutout, setQuakeCutout.type],
		[engineActions.updateQuakeCutout, updateQuakeCutout.type],
		[engineActions.setStormCutout, setStormCutout.type],
		[engineActions.updateStormCutout, updateStormCutout.type],
		[engineActions.setStormWindProbabilityKt, setStormWindProbabilityKt.type],
		[engineActions.setPaidEntitlement, setPaidEntitlement.type],
		[engineActions.openInfoWindow, openInfoWindow.type],
		[engineActions.updateSatellitePosition, updateSatellitePosition.type],
	]
	for (const [constant, actual] of pairs) {
		if (constant !== actual) {
			throw new Error(`engineActions out of sync: "${constant}" !== "${actual}" (update earthbrowser/engineBridge.ts)`)
		}
	}

	installEngineBridge({
		getState: () => appStore.getState(),
		dispatch: (action) => { appStore.dispatch(action as UnknownAction) },
		subscribe: (listener) => appStore.subscribe(listener),
		subscribeAction: (actionType, callback) => reduxActionBridge.subscribe(actionType, callback),
	})

	// Engine-originated user notifications go to the React toast system in
	// the app (the embed keeps the inert default notifier)
	installEngineNotifier({
		toast: (options) => { toast(options) },
		weatherAlerts: (updates) => showWeatherAlertToast(updates),
		stormAdvisories: (updates) => showStormAdvisoryToast(updates),
	})
}
